import { useEffect } from 'react';
import { PLANS } from '@agentbrush/shared';

const SITE_URL = 'https://agentbrush.dev';

const TITLE = 'AgentBrush: Great code still needs a face';
const DESCRIPTION =
  'Image generation built for coding agents. Let your agent create on-brand visuals for your projects, right inside Claude or Cursor.';

const META_TAGS: Record<string, string> = {
  description: DESCRIPTION,
  'og:title': TITLE,
  'og:description': DESCRIPTION,
  'og:type': 'website',
  'og:url': SITE_URL,
  'og:image': `${SITE_URL}/og-image.png?v=2`,
  'og:image:alt': 'AgentBrush. Great code still needs a face.',
  'twitter:card': 'summary_large_image',
  'twitter:title': TITLE,
  'twitter:description': DESCRIPTION,
  'twitter:image': `${SITE_URL}/og-image.png?v=2`,
};

const JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'AgentBrush',
  description:
    'Image generation for coding agents with presets, reference images, mask editing, and multi-CLI support.',
  url: SITE_URL,
  applicationCategory: 'DeveloperApplication',
  operatingSystem: 'Web',
  offers: (['starter', 'pro', 'power'] as const).map((tier) => ({
    '@type': 'Offer' as const,
    name: PLANS[tier].name,
    price: (PLANS[tier].price / 100).toFixed(2),
    priceCurrency: 'USD',
    description: `${PLANS[tier].tokens} tokens per month`,
  })),
};

export function LandingHead() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = TITLE;

    const createdElements: HTMLElement[] = [];
    const previousContent: Array<{ el: HTMLMetaElement; content: string | null }> = [];

    for (const [key, content] of Object.entries(META_TAGS)) {
      const isOg = key.startsWith('og:');
      const attr = isOg ? 'property' : 'name';
      let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
      if (el) {
        previousContent.push({ el, content: el.getAttribute('content') });
      } else {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        createdElements.push(el);
      }
      el.setAttribute('content', content);
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(JSON_LD);
    document.head.appendChild(script);
    createdElements.push(script);

    return () => {
      document.title = previousTitle;
      for (const el of createdElements) {
        el.remove();
      }
      for (const { el, content } of previousContent) {
        if (content !== null) {
          el.setAttribute('content', content);
        } else {
          el.removeAttribute('content');
        }
      }
    };
  }, []);

  return null;
}
